import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '@cart/api-data-access';
import { DbAgentClient } from './db-agent.client';
import { BackupAlertService } from './backup-alert.service';

/** Warn past this, page past the critical one. */
const LAG_WARNING_SECONDS = 5 * 60;
const LAG_CRITICAL_SECONDS = 15 * 60;

export interface RpoReading {
  /** Seconds since the newest segment was acknowledged by the repository. */
  measuredSeconds: number | null;
  lastUploadedAt: Date | null;
  pendingCount: number;
  /** Rising pending count over consecutive samples = a draining failure. */
  backlogGrowing: boolean;
}

/**
 * Reconciles archived WAL into `wal_archive_segments`, and computes the only
 * RPO figure anyone should quote.
 *
 * WHY RPO IS MEASURED HERE RATHER THAN READ FROM CONFIG
 *
 * `archive_timeout=300s` forces a segment SWITCH every five minutes. It does
 * not bound data loss. Real worst-case loss is:
 *
 *     time since the last segment closed      (<= archive_timeout)
 *   + archive-push queue latency              (async — seconds to minutes)
 *   + R2 upload and ack                       (network)
 *   + any backlog currently draining          (UNBOUNDED during an incident)
 *
 * The last term is the one that hurts: if archive-push starts failing at 03:00,
 * the true RPO grows by a minute every minute until somebody notices, and no
 * setting anywhere prevents that. So the number the Overview tile shows is
 * `now() - max(uploadedAt)`, sampled here, and the alert below is what actually
 * enforces the objective. The config value is an input to the number, not the
 * number.
 */
@Injectable()
export class WalMonitorService {
  private readonly logger = new Logger(WalMonitorService.name);

  /** Previous pending counts per target, for backlog-direction detection. */
  private readonly pendingHistory = new Map<string, number[]>();

  constructor(
    private readonly prisma: PrismaService,
    private readonly agent: DbAgentClient,
    private readonly alerts: BackupAlertService
  ) {}

  @Cron('*/2 * * * *')
  async sweep(): Promise<void> {
    if (!this.agent.isConfigured()) return;

    const targets = await this.prisma.backupTarget.findMany({ where: { isActive: true } });
    for (const target of targets) {
      try {
        await this.reconcile(target.id, target.stanza);
        await this.evaluate(target.id);
      } catch (err) {
        this.logger.warn(`WAL sweep failed for ${target.stanza}: ${(err as Error).message}`);
      }
    }
  }

  /**
   * Pull the archive range pgBackRest reports and mark our rows uploaded.
   *
   * pgBackRest reports a RANGE (min..max) per timeline rather than a segment
   * list, so this marks everything at or below `max` as uploaded. That is
   * sound because WAL archiving is strictly ordered — a segment cannot be
   * archived before its predecessor — and it is far cheaper than listing a
   * bucket every two minutes, which on R2 also costs Class A operations we are
   * budgeted against.
   */
  async reconcile(targetId: string, stanza: string): Promise<void> {
    const repo = await this.prisma.backupRepository.findFirst({ where: { kind: 's3', isActive: true } });
    const info = await this.agent.backupInfo(stanza, repo?.repoIndex);

    for (const stanzaInfo of info) {
      for (const archive of stanzaInfo.archive ?? []) {
        if (!archive.max) continue;

        const timeline = this.timelineOf(archive.max);
        const maxSegment = archive.max;

        const pending = await this.prisma.walArchiveSegment.findMany({
          where: { targetId, timeline, status: { in: ['pending', 'archived'] } },
        });

        for (const segment of pending) {
          // Lexicographic comparison is correct here: WAL segment names are
          // fixed-width uppercase hex, so string order is numeric order.
          if (segment.segmentName > maxSegment) continue;

          const uploadedAt = new Date();
          const lagSeconds = segment.archivedAt
            ? Math.max(0, Math.round((uploadedAt.getTime() - segment.archivedAt.getTime()) / 1000))
            : null;

          await this.prisma.walArchiveSegment.update({
            where: { id: segment.id },
            data: { status: 'uploaded', uploadedAt, lagSeconds, repositoryId: repo?.id ?? null },
          });
        }
      }
    }
  }

  /**
   * Record a segment as closed by Postgres but not yet confirmed in the repo.
   *
   * Called by the archive hook. Without a row here a segment that is never
   * uploaded is invisible — and an invisible missing segment is precisely the
   * failure that silently voids point-in-time recovery.
   */
  async recordArchived(targetId: string, segmentName: string, sizeBytes: number): Promise<void> {
    const timeline = this.timelineOf(segmentName);
    await this.prisma.walArchiveSegment.upsert({
      where: { targetId_segmentName_timeline: { targetId, segmentName, timeline } },
      create: { targetId, segmentName, timeline, sizeBytes, status: 'archived', archivedAt: new Date() },
      update: { status: 'archived', archivedAt: new Date(), sizeBytes },
    });
  }

  /** The live RPO reading, for the Overview tile and the alert below. */
  async rpo(targetId: string): Promise<RpoReading> {
    const newest = await this.prisma.walArchiveSegment.findFirst({
      where: { targetId, status: 'uploaded', uploadedAt: { not: null } },
      orderBy: { uploadedAt: 'desc' },
      select: { uploadedAt: true },
    });

    const pendingCount = await this.prisma.walArchiveSegment.count({
      where: { targetId, status: { in: ['pending', 'archived'] } },
    });

    const history = this.pendingHistory.get(targetId) ?? [];
    history.push(pendingCount);
    if (history.length > 3) history.shift();
    this.pendingHistory.set(targetId, history);

    // Three consecutive rises, not one: a single sample can rise simply because
    // a segment closed between the sample and the push.
    const backlogGrowing =
      history.length === 3 && history[0] < history[1] && history[1] < history[2] && history[2] > 2;

    return {
      measuredSeconds: newest?.uploadedAt
        ? Math.round((Date.now() - newest.uploadedAt.getTime()) / 1000)
        : null,
      lastUploadedAt: newest?.uploadedAt ?? null,
      pendingCount,
      backlogGrowing,
    };
  }

  private async evaluate(targetId: string): Promise<void> {
    const reading = await this.rpo(targetId);

    // A growing backlog is the unbounded-RPO case and deserves its own alert:
    // the lag number may still look fine while the queue behind it is running
    // away, and by the time the lag reflects it the loss window is already large.
    if (reading.backlogGrowing) {
      await this.alerts.raise({
        kind: 'archive_backlog',
        severity: 'critical',
        targetId,
        title: `WAL archive backlog is growing (${reading.pendingCount} segments unconfirmed)`,
        detail: {
          pendingCount: reading.pendingCount,
          measuredRpoSeconds: reading.measuredSeconds,
          note: 'Real RPO grows for as long as this persists. archive_timeout does not bound it.',
        },
      });
    } else {
      await this.alerts.resolve('archive_backlog', targetId);
    }

    const lag = reading.measuredSeconds;
    if (lag === null) {
      // No segment has ever been confirmed. On a fresh install that is normal
      // and not worth paging about; the SLA check on backup runs covers the
      // "nothing is working at all" case.
      return;
    }

    if (lag > LAG_CRITICAL_SECONDS) {
      await this.alerts.raise({
        kind: 'wal_lag',
        severity: 'critical',
        targetId,
        title: `Measured RPO is ${Math.round(lag / 60)} minutes`,
        detail: { measuredRpoSeconds: lag, lastUploadedAt: reading.lastUploadedAt?.toISOString() },
      });
    } else if (lag > LAG_WARNING_SECONDS) {
      await this.alerts.raise({
        kind: 'wal_lag',
        severity: 'warning',
        targetId,
        title: `Measured RPO is ${Math.round(lag / 60)} minutes`,
        detail: { measuredRpoSeconds: lag, lastUploadedAt: reading.lastUploadedAt?.toISOString() },
      });
    } else {
      await this.alerts.resolve('wal_lag', targetId);
    }
  }

  /**
   * Timeline is the first 8 hex digits of a WAL segment name.
   * 000000010000000000000042 -> timeline 1.
   */
  private timelineOf(segmentName: string): number {
    return parseInt(segmentName.slice(0, 8), 16) || 1;
  }
}
